'use client';

import React, { useRef, useState } from 'react';
import { useColors } from '../theme/ThemeProvider';
import { fontSize, fontWeight, letterSpacing, radius, space } from '../theme/tokens';
import { parseNumericInput } from './parseNumber';

export interface NumInputProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  unit: string;
  /** Step size for single-click adjustment. Defaults to 1. */
  step?: number;
  /** Accessible label for the typed value (e.g. "Gewicht"). */
  label?: string;
}

/**
 * Stepper input with × / ×10 buttons on either side.
 * Used in onboarding for age/weight/height entry.
 *
 * Die Zahl ist ein echtes Eingabefeld: antippen öffnet die numerische Tastatur,
 * man kann den Wert direkt tippen. Die Stepper bleiben für die schnelle
 * Feinjustierung.
 *
 * ── Draft-Modell ──
 * Während der Eingabe hält `draft` den rohen Text (auch ungültige Zwischen-
 * zustände wie "" oder "7,"). Erst bei Blur/Enter wird geparst, auf [min,max]
 * geclamped und `onChange` gefeuert; danach zeigt das Feld wieder den
 * formatierten `value` aus dem Parent. Solange nicht getippt wird, ist `draft`
 * null — Stepper-Updates von außen kommen also sofort durch.
 */
export function NumInput({ value, onChange, min, max, unit, step = 1, label }: NumInputProps) {
  const colors = useColors();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string | null>(null);

  const clamp = (n: number) => Math.max(min, Math.min(max, n));

  const adjust = (delta: number) => {
    // Eine laufende Texteingabe hat Vorrang vor dem alten Prop-Wert.
    const base = draft !== null ? parseNumericInput(draft) ?? value : value;
    const next = Number((base + delta).toFixed(1));
    setDraft(null);
    onChange(clamp(next));
  };

  const format = (n: number) => (step < 1 ? n.toFixed(1).replace('.', ',') : String(n));

  const commit = () => {
    if (draft === null) return;
    const parsed = parseNumericInput(draft);
    setDraft(null);
    // Leer oder unlesbar → Wert bleibt, wie er war (kein stiller Sprung auf min).
    if (parsed === null || draft.trim() === '') return;
    onChange(clamp(Number(parsed.toFixed(1))));
  };

  const display = draft !== null ? draft : format(value);

  /**
   * Verhindert, dass ein Stepper-Tipp den Fokus aus dem Feld nimmt.
   *
   * Ohne das feuert beim Antippen zuerst `blur` (also `commit`) und erst danach
   * der Klick. `adjust` läse dann den noch nicht aktualisierten `value` aus dem
   * Prop und überschriebe die gerade getippte Zahl: 75 tippen, „›" tippen —
   * heraus kam 86 statt 76.
   */
  const keepFocus = (e: React.PointerEvent) => e.preventDefault();

  const stepperStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: radius.md,
    border: `1.5px solid ${colors.borderStrong}`,
    background: colors.surface,
    fontSize: fontSize.lg,
    color: colors.text,
    cursor: 'pointer',
    fontFamily: 'inherit',
    flexShrink: 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: space[3],
        margin: `${space[5]}px 0`,
      }}
    >
      <button
        type="button"
        onPointerDown={keepFocus}
        onClick={() => adjust(-step * 10)}
        style={stepperStyle}
        aria-label={`−10 ${unit}`}
      >
        «
      </button>
      <button
        type="button"
        onPointerDown={keepFocus}
        onClick={() => adjust(-step)}
        style={stepperStyle}
        aria-label={`−${step} ${unit}`}
      >
        ‹
      </button>

      <div style={{ textAlign: 'center', minWidth: 100, flex: '0 1 auto' }}>
        <input
          ref={inputRef}
          type="text"
          // 'decimal' blendet auf iOS das Komma ein — bei ganzzahligen Feldern
          // (Alter, Größe) reicht der reine Ziffernblock.
          inputMode={step < 1 ? 'decimal' : 'numeric'}
          value={display}
          aria-label={label ? `${label} in ${unit}` : unit}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => {
            setDraft(format(value));
            // Antippen soll den Wert ersetzen, nicht anhängen.
            requestAnimationFrame(() => inputRef.current?.select());
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              inputRef.current?.blur();
            }
          }}
          style={{
            width: '100%',
            textAlign: 'center',
            border: 'none',
            borderBottom: `1.5px dashed ${draft !== null ? colors.accent : colors.border}`,
            background: 'transparent',
            padding: 0,
            fontSize: fontSize['5xl'],
            fontWeight: fontWeight.extrabold,
            color: colors.text,
            letterSpacing: letterSpacing.tight,
            fontFamily: 'inherit',
            outline: 'none',
            borderRadius: 0,
            // iOS zoomt sonst beim Fokus in kleine Felder — hier unnötig, die
            // Schrift ist ohnehin groß.
            WebkitAppearance: 'none',
          }}
        />
        <div
          style={{
            fontSize: fontSize.lg,
            fontWeight: fontWeight.semibold,
            color: colors.textDim,
            marginTop: 2,
          }}
        >
          {unit}
        </div>
      </div>

      <button
        type="button"
        onPointerDown={keepFocus}
        onClick={() => adjust(step)}
        style={stepperStyle}
        aria-label={`+${step} ${unit}`}
      >
        ›
      </button>
      <button
        type="button"
        onPointerDown={keepFocus}
        onClick={() => adjust(step * 10)}
        style={stepperStyle}
        aria-label={`+10 ${unit}`}
      >
        »
      </button>
    </div>
  );
}
